/**
 * The first-level divisions of a country an address names - a US state,
 * later a Canadian province or an Australian state - kept by ISO 3166-1
 * alpha-2 country code. An address stores the division's code (the ISO
 * 3166-2 part after the dash, which for the US is the USPS code: `NY`);
 * the field shows and searches the name. Supporting a country is one entry
 * in `REGION_CATALOGUE` here and in the API's `core/lib/regions.ts`, which
 * refuses a US region it does not list; a country without one takes the region as typed.
 */

export type RegionKind = 'state' | 'district' | 'territory' | 'province'

export interface Region {
  /** As the address stores it: `NY`. */
  code: string
  name: string
  kind: RegionKind
}

export interface CountryRegions {
  /** The country's name as a form shows it: "United States". */
  name: string
  /** What a form calls the field for this country: "State", "Province". */
  label: string
  regions: readonly Region[]
}

const US_STATES: readonly (readonly [code: string, name: string])[] = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'], ['CA', 'California'],
  ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'], ['FL', 'Florida'], ['GA', 'Georgia'],
  ['HI', 'Hawaii'], ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'],
  ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'], ['MD', 'Maryland'],
  ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'], ['MS', 'Mississippi'], ['MO', 'Missouri'],
  ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'],
  ['NM', 'New Mexico'], ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'],
  ['OK', 'Oklahoma'], ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'],
  ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'], ['VT', 'Vermont'],
  ['VA', 'Virginia'], ['WA', 'Washington'], ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
]

const US_REGIONS: readonly Region[] = [
  ...US_STATES.map(([code, name]): Region => ({ code, name, kind: 'state' })),
  { code: 'DC', name: 'District of Columbia', kind: 'district' },
  { code: 'AS', name: 'American Samoa', kind: 'territory' },
  { code: 'GU', name: 'Guam', kind: 'territory' },
  { code: 'MP', name: 'Northern Mariana Islands', kind: 'territory' },
  { code: 'PR', name: 'Puerto Rico', kind: 'territory' },
  { code: 'VI', name: 'U.S. Virgin Islands', kind: 'territory' },
]

/** Every country whose divisions the platform lists. A new country is a line here. */
export const REGION_CATALOGUE: Readonly<Record<string, CountryRegions>> = {
  US: { name: 'United States', label: 'State', regions: US_REGIONS },
}

const FALLBACK_LABEL = 'State or province'

/** The countries an address may be in, by code: those whose divisions are listed. One today, so the country is not a choice yet. */
export const SUPPORTED_COUNTRIES: readonly string[] = Object.keys(REGION_CATALOGUE)

/** "United States" for `US`; the code itself for a country not listed. */
export function countryNameOf(country: string): string {
  return regionsOf(country)?.name ?? country
}

/** The divisions of a country, or null when the platform does not list them (the region is then typed). */
export function regionsOf(country: string): CountryRegions | null {
  return REGION_CATALOGUE[country.trim().toUpperCase()] ?? null
}

/** "State" for the US, "State or province" for a country not listed. */
export function regionLabelOf(country: string): string {
  return regionsOf(country)?.label ?? FALLBACK_LABEL
}

/** The division a text names exactly, by code or by name, ignoring case; null when none does. */
export function findRegion(country: string, text: string): Region | null {
  const wanted = text.trim().toLowerCase()
  if (wanted === '') {
    return null
  }
  return regionsOf(country)?.regions.find((region) => region.code.toLowerCase() === wanted || region.name.toLowerCase() === wanted) ?? null
}

/** True for a listed country's code, or for any text at all in a country not listed. */
export function isKnownRegion(country: string, value: string): boolean {
  return regionsOf(country) === null ? value.trim() !== '' : findRegion(country, value)?.code === value.trim().toUpperCase()
}

/** "New York" for `NY`; the value itself when the country or the code is not listed. */
export function regionNameOf(country: string, value: string): string {
  return findRegion(country, value)?.name ?? value
}

/** How well a division answers a query: 0 its code, 1 its name's start, 2 a later word's start, null not at all. */
function matchRank(region: Region, query: string): number | null {
  const name = region.name.toLowerCase()
  if (region.code.toLowerCase() === query) {
    return 0
  }
  if (name.startsWith(query)) {
    return 1
  }
  return name.split(/[\s.]+/).some((word) => word.startsWith(query)) ? 2 : null
}

/** The divisions a query names, best first then alphabetically: "new" finds the four News, "car" both Carolinas, "ny" New York. Every one for an empty query. */
export function matchRegions(country: string, query: string): Region[] {
  const regions = regionsOf(country)?.regions ?? []
  const wanted = query.trim().toLowerCase()
  if (wanted === '') {
    return [...regions].sort((a, b) => a.name.localeCompare(b.name))
  }
  return regions
    .map((region) => ({ region, rank: matchRank(region, wanted) }))
    .filter((entry): entry is { region: Region; rank: number } => entry.rank !== null)
    .sort((a, b) => a.rank - b.rank || a.region.name.localeCompare(b.region.name))
    .map((entry) => entry.region)
}
